import { AppContext, CacheIsolation } from '@/utils/contextCacheManager';
import { PublicApiSingleton } from '../providers/base/PublicApiSingleton';
import { clientLogger } from '@/lib/client-logger';

/**
 * Location status info returned by public API
 * Mirrors API's LocationStatusInfo from location-status.ts
 */
export interface LocationStatusInfo {
  status: string;
  label: string;
  description: string;
  color: 'green' | 'yellow' | 'orange' | 'red' | 'gray';
  icon: string;
  canSync: boolean;
  showInDirectory: boolean;
  showStorefront: boolean;
  countsTowardLimits: boolean;
  shouldBeBilled: boolean;
}

/**
 * Subscription status info returned by public API
 * Used for subscription-based panel display
 */
export interface SubscriptionStatusInfo {
  status: 'canceled' | 'past_due';
  label: string;
  description: string;
  showStorefront: boolean;
  showInDirectory: boolean;
  gracePeriodEnded?: boolean;
}

/**
 * Public tenant info returned by /api/public/tenant/:identifier
 * Used for storefront visibility control
 */
export interface PublicTenantInfo {
  id: string;
  name: string;
  slug: string | null;
  subscriptionStatus: string;
  subscriptionTier?: string | null;
  trialEndsAt?: string | null;
  locationStatus?: string | null;
  statusInfo?: LocationStatusInfo;
  organizationId?: string | null;
  statusChangedAt?: string | null;
  subscriptionStatusInfo?: SubscriptionStatusInfo | null;
  showSubscriptionPanel?: boolean;
  hasDirectory: boolean;
  directoryData?: any;
  profileData?: any;
  metadata?: any;
  logoUrl?: string;
  bannerUrl?: string;
  createdAt: string;
  updatedAt: string;
  isDemo?: boolean;
  demoExpiresAt?: string | null;
}

export interface TenantProfile {
  id: string;
  business_name: string;
  address_line1: string;
  city: string;
  postal_code: string;
  country_code: string;
  phone_number: string;
  email: string;
  website?: string;
  latitude?: number;
  longitude?: number;
  business_description?: string;
  hours?: any;
  logo_url?: string;
  social_links?: any;
  seo_tags?: any;
  // Additional fields for compatibility
  name?: string;
  slug?: string;
  domain?: string;
  logo?: string;
  banner?: string;
  description?: string;
  address?: {
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
  contact?: {
    email: string;
    phone: string;
    website?: string;
  };
  settings?: {
    theme: string;
    currency: string;
    timezone: string;
  };
  status: 'active' | 'inactive' | 'pending';
  createdAt: string;
  updatedAt: string;
}

/**
 * Tenant Public Service
 * Handles public tenant data operations with proper caching
 * Used for public pages and public tenant information display
 */
class TenantPublicService extends PublicApiSingleton {

    protected defaultContext: AppContext = AppContext.STORE;
    protected defaultIsolation: CacheIsolation = CacheIsolation.STORE;
  private static instance: TenantPublicService;

  // TTL for public tenant data (longer since it changes less frequently)
  private readonly PROFILE_TTL = 15 * 60 * 1000; // 15 minutes for public profiles
  private readonly HOURS_TTL = 10 * 60 * 1000; // 10 minutes for business hours

  protected constructor() {
    super('tenant-public-singleton');
  }

  public static getInstance(): TenantPublicService {
    if (!TenantPublicService.instance) {
      TenantPublicService.instance = new TenantPublicService();
    }
    return TenantPublicService.instance;
  }

  /**
   * Get public tenant info (basic)
   * Uses the /api/public/tenant/:tenantId endpoint
   */
  async getPublicTenantInfo(tenantId: string): Promise<PublicTenantInfo | null> {
    try {
      const response = await this.makeDefaultRequest<PublicTenantInfo>(
        `/api/public/tenant/${tenantId}`,
        {},
        `public-tenant-info-${tenantId}`,
        this.PROFILE_TTL
      );

      if (!response.success){
        clientLogger.error('[TenantPublicService] Failed to get public tenant info:', { detail: response.error });
        return null;
      }

      // The API returns { success, data: { id, name, locationStatus, ... }, metadata }
      // makeDefaultRequest wraps this, so response.data contains the full API response
      // The actual tenant data is in response.data.data
      const data = response.data as any;
      return data?.data || data;
    } catch (error) {
      clientLogger.error('[TenantPublicService] Failed to get public tenant info:', { detail: error });
      return null;
    }
  }

  /**
   * Storefront canonical ladder (sprint plan 4.2): the API resolves tenant
   * state — kind 'custom' (active custom domain), 'subdomain', or 'path'.
   * Best-effort: null on any failure so metadata never blocks render.
   */
  async getStorefrontCanonical(idOrSlug: string): Promise<{ kind: 'custom' | 'subdomain' | 'path'; host?: string; tenantId?: string } | null> {
    try {
      const response = await this.makeDefaultRequest<any>(
        `/api/tenants/storefront-canonical/${encodeURIComponent(idOrSlug)}`,
        {},
        `storefront-canonical-${idOrSlug}`,
        this.PROFILE_TTL
      );
      if (!response.success || !response.data?.kind) return null;
      return { kind: response.data.kind, host: response.data.host, tenantId: response.data.tenantId };
    } catch {
      return null;
    }
  }

  /**
   * Get tenant logo (public)
   * Uses the /api/public/tenant/:tenantId/logo endpoint
   */
  async getTenantLogo(tenantId: string): Promise<any | null> {
    try {
      const response = await this.makeDefaultRequest<any>(
        `/api/public/tenant/${encodeURIComponent(tenantId)}/logo`,
        {},
        `public-tenant-logo-${tenantId}`,
        this.PROFILE_TTL
      );

      if (!response.success){
        clientLogger.error('[TenantPublicService] Failed to get tenant logo:', { detail: response.error });
        return null;
      }

    //  console.log(`${this.constructor.name} - Logo Response:`, response.data);

      return response.data;
    } catch (error) {
      clientLogger.error('[TenantPublicService] Failed to get tenant logo:', { detail: error });
      return null;
    }
  }

  /**
   * Get tenant tier information (public)
   * Uses the /api/public/tenants/:tenantId/tier endpoint (no auth required)
   */
  async getPublicTenantTier(tenantId: string): Promise<any | null> {
    try {
      const response = await this.makeDefaultRequest<any>(
        `/api/public/tenants/${tenantId}/tier`,
        {},
        `public-tenant-tier-${tenantId}`,
        this.PROFILE_TTL
      );

      if (!response.success){
        clientLogger.error('[TenantPublicService] Failed to get public tenant tier:', { detail: response.error });
        return null;
      }
   //   console.log(`${this.constructor.name} - Tier Response:`, response.data);

      return response.data;
    } catch (error) {
      clientLogger.error('[TenantPublicService] Failed to get public tenant tier:', { detail: error });
      return null;
    }
  }

  /**
   * Get public tenant profile
   * Uses the /api/public/tenant/:tenant_id/profile endpoint for public profile data
   */
  async getPublicTenantProfile(tenantId: string): Promise<TenantProfile | null> {
    try {
      const response = await this.makeDefaultRequest<TenantProfile>(
        `/api/public/tenant/${tenantId}/profile`,
        {},
        `public-tenant-profile-${tenantId}`,
        this.PROFILE_TTL
      );
      if (!response.success){
        clientLogger.error('[TenantPublicService] Failed to get public tenant profile:', { detail: response.error });
        return null;
      }
    //  console.log(`${this.constructor.name} - Profile Response:`, response.data);

      return response.data||null;
    } catch (error) {
      clientLogger.error('[TenantPublicService] Failed to get public tenant profile:', { detail: error });
      return null;
    }
  }

  /**
   * Get tenant business hours (public)
   * Uses the /api/public/tenant/:tenantId/business-hours/status endpoint
   */
  async getTenantBusinessHours(tenantId: string): Promise<any | null> {
    try {
      const response = await this.makeDefaultRequest<any>(
        `/api/public/tenant/${tenantId}/business-hours/status`,
        {},
        `tenant-hours-${tenantId}`,
        this.HOURS_TTL
      );

      if (!response.success){
        clientLogger.error('[TenantPublicService] Failed to get tenant business hours:', { detail: response.error });
        return null;
      }
    //  console.log(`${this.constructor.name} - Hours Response:`, response.data);

      return response.data;
    } catch (error) {
      clientLogger.error('[TenantPublicService] Failed to get tenant business hours:', { detail: error });
      return null;
    }
  }

  /**
   * Get tenant business hours from the business-hours API (public)
   * Uses the /api/business-hours/:tenantId endpoint (same as shops page)
   */
  async getBusinessHours(tenantId: string): Promise<any | null> {
    try {
      const response = await this.makeDefaultRequest<any>(
        `/api/business-hours/${tenantId}`,
        {},
        `business-hours-v2-${tenantId}`, // v2 for timezone enhancement
        this.HOURS_TTL
      );

      if (!response.success){
        clientLogger.error('[TenantPublicService] Failed to get business hours:', { detail: response.error });
        return null;
      }

      return response.data || null;
    } catch (error) {
      clientLogger.error('[TenantPublicService] Failed to get business hours:', { detail: error });
      return null;
    }
  }

  /**
   * Get tenant basic info for directory listing
   * Uses cached public profile data
   */
  async getTenantDirectoryInfo(tenantId: string): Promise<{
    id: string;
    name: string;
    slug: string;
    description?: string;
    logo?: string;
    address?: any;
    contact?: any;
  } | null> {
    try {
      const profile = await this.getPublicTenantProfile(tenantId);
      if (!profile) return null;

//      console.log(`${this.constructor.name} - Directory Info Response:`, profile);

      return {
        id: profile.id,
        name: profile.business_name || profile.name || '',
        slug: profile.slug || '',
        description: profile.business_description || profile.description,
        logo: profile.logo_url || profile.logo,
        address: profile.address,
        contact: profile.contact
      };
    } catch (error) {
      clientLogger.error('[TenantPublicService] Failed to get tenant directory info:', { detail: error });
      return null;
    }
  }

  /**
   * Invalidate public tenant cache
   */
  public async invalidatePublicTenantCache(tenantId: string): Promise<void> {
    const patterns = [
      `public-tenant-info-${tenantId}`,
      `public-tenant-profile-${tenantId}`,
      `tenant-hours-${tenantId}`
    ];

    for (const pattern of patterns) {
      await this.invalidateCache(pattern);
    }
  }
}

// Export singleton instance
export const tenantPublicService = TenantPublicService.getInstance();

// Export cache invalidation helpers for external use
export const invalidatePublicTenantCache = async (tenantId: string): Promise<void> => {
  await tenantPublicService.invalidatePublicTenantCache(tenantId);
};
